// InvitationForm.tsx
import React, { useState, useEffect } from 'react';
import { useInvitationContext } from '../../../contexts/InvitationContext';
import styles from '../../../styles/ManageAgency.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';

interface InvitationFormProps {
  onSubmit: (email: string, role: string) => void;
  onCancel: () => void;
}

const InvitationForm: React.FC<InvitationFormProps> = ({ onSubmit, onCancel }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [inviteError, setInviteError] = useState('');
  
  const { fetchUserTypes, userTypes: contextUserTypes, checkUserExists, isLoading } = useInvitationContext();

  // Super Admin is reserved for the internal super admin portal login and
  // must never be selectable here, even if a stale/unfiltered list slips
  // through from the backend.
  const filterInvitable = (types: typeof contextUserTypes) => (types || []).filter(type => {
    const name = (type.name || '').trim().toLowerCase();
    const displayName = (type.display_name || '').trim().toLowerCase();
    return name !== 'super admin' && displayName !== 'super admin';
  });

  useEffect(() => {
    // If context already has user types, use them; otherwise trigger fetch (cached in provider)
    if (contextUserTypes && contextUserTypes.length > 0) {
      const invitable = filterInvitable(contextUserTypes);
      if (!role && invitable.length > 0) setRole(invitable[0].id);
      return;
    }

    let mounted = true;
    (async () => {
      const res = await fetchUserTypes();
      if (!mounted) return;
      if (res.success && res.types && res.types.length > 0) {
        const invitable = filterInvitable(res.types);
        if (!role && invitable.length > 0) setRole(invitable[0].id);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextUserTypes, fetchUserTypes, role]);

  const isValidEmail = (email: string) => {
    // Simple email regex for validation
    return Boolean(email) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError('');
    
    if (!isValidEmail(email)) {
      setInviteError('Please enter a valid email address');
      return;
    }
    
    const { exists } = await checkUserExists(email);
    if (exists) {
      setInviteError('User already exists');
      return;
    }
    onSubmit(email, role);
  };

  const typesToRender = filterInvitable(contextUserTypes);
  if (typesToRender.length === 0 && isLoading) {
    return <div>Loading user types...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className={styles.inviteForm}>
      <label className={styles.inviteLabel}>
        User Email:
        {inviteError && <span className={styles.error}>{inviteError}</span>}
        <input
          type="email"
          value={email}
          required
          onChange={e => setEmail(e.target.value)}
          className={styles.inviteInput}
        />
      </label>
      <label className={styles.inviteLabel}>
        Role:
        <select value={role} onChange={e => setRole(e.target.value)} required className={styles.inviteSelect}>
          {typesToRender.map(type => (
            <option key={type.id} value={type.id}>{type.display_name}</option>
          ))}
        </select>
      </label>
      <div className={styles.inviteActions}>
        <button type="button" className={`${buttonStyles.button} ${buttonStyles.secondary}`} onClick={onCancel}>Cancel</button>
        <button
          type="submit"
          className={`${buttonStyles.button} ${buttonStyles.primary}`}
          disabled={!isValidEmail(email) || !role || isLoading}
        >
          {isLoading ? 'Sending...' : 'Send Invitation'}
        </button>
      </div>
    </form>
  );
};

export default InvitationForm;
